import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo } from 'react'

import { fetchFlightCalendar } from 'actions/FlightActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FlightsClassTypes } from 'constants/flight'
import { FlightsFareTypes } from 'constants/flight'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { endOfMonth, startOfMonth } from 'lib/datetime/dateUtils'
import { getFlightCalendarKey } from 'lib/flights/flightUtils'

interface Options {
  departureAirportCode?: string
  arrivalAirportCode?: string
  fareType?: string
  fareClass?: FlightsClassTypes
  /** Set on flight deal pages, where the calendar deliberately shows no fares. */
  disabled?: boolean
  minDate: Date
  maxDate: Date
}

export interface FlightCalendarPrices {
  /** Cached return fare by ISO date, in whole currency units. */
  cachedPriceFor: (isoDate: string) => number | undefined
  /** The lowest cached fare in a given `YYYY-MM`, for the "cheapest" marker. */
  isCheapestInMonth: (isoDate: string) => boolean
  loading: boolean
  /**
   * Whether fares should be shown. A search can carry cached fares it is not
   * allowed to display — flight deal pages set `disableCalendarPricing` — and
   * the cache is shared, so skipping the fetch is not enough on its own.
   */
  arePricesVisible: boolean
  earliestBookingDate?: string
  finalBookingDate?: string
}

export default function useFlightCalendarPrices(options: Options): FlightCalendarPrices {
  const {
    departureAirportCode,
    arrivalAirportCode,
    fareType,
    fareClass,
    disabled = false,
    minDate,
    maxDate,
  } = options

  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const isPriceable = !disabled &&
    !!departureAirportCode &&
    !!arrivalAirportCode &&
    fareType !== FlightsFareTypes.MULTI_CITY &&
    fareType !== FlightsFareTypes.ONE_WAY

  const startDate = useMemo(() => moment(startOfMonth(minDate)).format(ISO_DATE_FORMAT), [minDate])
  const endDate = useMemo(() => moment(endOfMonth(maxDate)).format(ISO_DATE_FORMAT), [maxDate])
  const tripType = fareType === FlightsFareTypes.ONE_WAY ? 'OneWay' as const : 'Return' as const

  const cacheKey = getFlightCalendarKey({
    startDate,
    endDate,
    origin: departureAirportCode ?? '',
    destination: arrivalAirportCode ?? '',
    region: currentRegionCode,
    currency: currentCurrency,
    tripType,
    fareClass,
  })

  const flightCalendar = useAppSelector(state => state.flights.flightCalendar[cacheKey])
  const loading = useAppSelector(state => !!state.flights.flightCalendarLoading[cacheKey])

  useEffect(() => {
    if (!isPriceable) return
    dispatch(fetchFlightCalendar({
      startDate,
      endDate,
      origin: departureAirportCode,
      destination: arrivalAirportCode,
      tripType,
      fareClass,
    }))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, isPriceable])

  const cachedPrices = flightCalendar?.cachedPrices

  const cheapestByMonth = useMemo(() => {
    const cheapest = new Map<string, number>()
    for (const [date, value] of Object.entries(cachedPrices ?? {})) {
      const month = date.slice(0, 7)
      const current = cheapest.get(month)
      if (current === undefined || value.cost < current) cheapest.set(month, value.cost)
    }
    return cheapest
  }, [cachedPrices])

  const cachedPriceFor = useCallback(
    (isoDate: string) => cachedPrices?.[isoDate]?.cost,
    [cachedPrices],
  )

  const isCheapestInMonth = useCallback((isoDate: string) => {
    const cost = cachedPrices?.[isoDate]?.cost
    return cost !== undefined && cheapestByMonth.get(isoDate.slice(0, 7)) === cost
  }, [cachedPrices, cheapestByMonth])

  /*
   * Held stable across renders: the calendar's `renderDay` and its day-click
   * handler both depend on this, so a fresh object each time would rebuild
   * them on every parent render and re-render every day cell on the grid.
   */
  return useMemo(() => ({
    cachedPriceFor,
    isCheapestInMonth,
    loading,
    arePricesVisible: !disabled,
    earliestBookingDate: flightCalendar?.earliestBookingDate,
    finalBookingDate: flightCalendar?.finalBookingDate,
  }), [
    cachedPriceFor,
    isCheapestInMonth,
    loading,
    disabled,
    flightCalendar?.earliestBookingDate,
    flightCalendar?.finalBookingDate,
  ])
}
